import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { FormLabel } from "../../components/form-label/FormLabel";
import { tenantService } from "../../services/tenantService";
import { flowService } from "../../services/flowService";
import { Conversation, Flow, Tenant } from "../../types";

interface ConvDoc extends Conversation {
  id: string;
}

const STATUS_COLOR: Record<string, "success" | "warning" | "error" | "default"> = {
  active: "warning",
  completed: "success",
  failed: "error",
};

export default function ConversationList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantIdParam = searchParams.get("tenantId") ?? "";
  const flowIdParam = searchParams.get("flowId") ?? "";

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [conversations, setConversations] = useState<ConvDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    tenantService
      .list()
      .then((res) => setTenants(res.data.data.tenants))
      .catch((e) => {
        console.error(e);
      });
  }, []);

  // Load the tenant's flows to populate the per-flow filter dropdown.
  useEffect(() => {
    if (!tenantIdParam) {
      setFlows([]);
      return;
    }
    flowService
      .list(tenantIdParam)
      .then((res) => setFlows(res.data.data.flows))
      .catch((e) => console.error(e));
  }, [tenantIdParam]);

  useEffect(() => {
    if (!tenantIdParam) {
      setConversations([]);
      return;
    }
    setLoading(true);
    setError(null);
    const q = query(
      collection(db, "conversations"),
      where("tenant_id", "==", tenantIdParam),
      // Optional per-flow filter (requires the conversations composite index).
      ...(flowIdParam ? [where("flow_state.flow_id", "==", flowIdParam)] : []),
      orderBy("updated_at", "desc"),
      limit(50)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setConversations(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Conversation) }))
        );
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError("Errore nel caricamento delle conversazioni");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [tenantIdParam, flowIdParam]);

  const formatDate = (ts: { seconds: number } | undefined) =>
    ts ? new Date(ts.seconds * 1000).toLocaleString("it-IT") : "—";

  return (
    <div className="container">
      <FormLabel label="Conversazioni" />

      <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="tenant-filter">Tenant</InputLabel>
          <Select
            labelId="tenant-filter"
            label="Tenant"
            value={tenantIdParam}
            onChange={(e) =>
              // Changing tenant drops the flow filter (flows are tenant-scoped).
              setSearchParams(e.target.value ? { tenantId: e.target.value } : {})
            }
          >
            <MenuItem value="">— seleziona tenant —</MenuItem>
            {tenants.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 260 }} disabled={!tenantIdParam}>
          <InputLabel id="flow-filter">Flow</InputLabel>
          <Select
            labelId="flow-filter"
            label="Flow"
            value={flowIdParam}
            onChange={(e) =>
              setSearchParams(
                e.target.value
                  ? { tenantId: tenantIdParam, flowId: e.target.value }
                  : { tenantId: tenantIdParam }
              )
            }
          >
            <MenuItem value="">— tutti i flow —</MenuItem>
            {flows.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!tenantIdParam ? (
        <Alert severity="info">Seleziona un tenant per visualizzare le conversazioni.</Alert>
      ) : loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Utente (telefono)</TableCell>
                <TableCell>Stato flow</TableCell>
                <TableCell>Messaggi</TableCell>
                <TableCell>Aggiornato</TableCell>
                <TableCell align="right">Dettaglio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nessuna conversazione trovata
                  </TableCell>
                </TableRow>
              )}
              {conversations.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {c.user_phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {c.flow_state ? (
                      <Chip
                        label={c.flow_state.status}
                        color={STATUS_COLOR[c.flow_state.status] ?? "default"}
                        size="small"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{c.messages?.length ?? 0}</TableCell>
                  <TableCell>{formatDate(c.updated_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Vedi conversazione">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(
                            `/chatbot/monitoring/conversations/${encodeURIComponent(c.id)}?tenantId=${tenantIdParam}${flowIdParam ? `&flowId=${flowIdParam}` : ""}`
                          )
                        }
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}
